import { useCallback } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { CheckmarkOutline, ChevronDownFill } from '@lyearn/icons';
import { ActionMenuItemProps } from '@lyearn/molecules';
import { Box, Popover, Stack, Touchable, Typography, usePopoverContext } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';
import { AssignmentTypeEnum } from '@/types/schema';

type MenuItemType = ActionMenuItemProps & { description: string };

const getMenuItems = (t: TFunction): MenuItemType[] => [
  {
    id: AssignmentTypeEnum.Flexible,
    label: t('AssignmentCreator:RecurringSession.Flexible'),
    description: t('AssignmentCreator:RecurringSession.FlexibleDescription'),
  },
  {
    id: AssignmentTypeEnum.Specific,
    label: t('AssignmentCreator:RecurringSession.Specific'),
    description: t('AssignmentCreator:RecurringSession.SpecificDescription'),
  },
  {
    id: AssignmentTypeEnum.All,
    label: t('AssignmentCreator:RecurringSession.All'),
    description: t('AssignmentCreator:RecurringSession.AllDescription'),
  },
];

function MenuItem(props: {
  isSelected: boolean;
  value: MenuItemType;
  onClick: (val: AssignmentTypeEnum) => void;
}) {
  const { isSelected, value, onClick: onClickProp } = props;
  const { onClose } = usePopoverContext();

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      stopPropagation(e);
      onClickProp(value.id as AssignmentTypeEnum);
      onClose();
    },
    [onClickProp, onClose, value.id],
  );

  return (
    <Touchable
      key={value.id}
      className="flex justify-between gap-y-4 rounded-8 p-12 hover:bg-bg-hover"
      onClick={onClick}>
      <Box className="space-y-2">
        <Typography variant="body-short-02">{value.label}</Typography>
        <Typography color="text-text-secondary" variant="body-short-03">
          {value.description}
        </Typography>
      </Box>
      {isSelected ? <CheckmarkOutline className="h-20 w-20" fillColorToken="icon-active" /> : null}
    </Touchable>
  );
}

interface AssignmentTypeProps {
  assignmentType?: AssignmentTypeEnum;
  onChange: (assignmentType: AssignmentTypeEnum) => void;
}

function AssignmentType({
  assignmentType = AssignmentTypeEnum.All,
  onChange,
}: AssignmentTypeProps) {
  const { t } = useTranslation();
  const menuItems = getMenuItems(t);

  const currentItem = menuItems.find((item) => (item.id as AssignmentTypeEnum) === assignmentType);

  return (
    <Popover>
      <Popover.Button>
        <Touchable className="rounded-04 group flex flex-shrink-0 items-center p-4 pe-16">
          <Typography variant="body-short-02">{currentItem?.label}</Typography>
          <ChevronDownFill className="h-20 w-20 text-icon-secondary ms-2 group-hover:text-icon-primary" />
        </Touchable>
      </Popover.Button>
      <Popover.Content minWidth={22.8} position="bottom-end">
        <Stack className="p-8" direction="col" gapX={4}>
          {menuItems.map((value) => (
            <MenuItem
              key={value.id}
              isSelected={value.id === assignmentType}
              value={value}
              onClick={onChange}
            />
          ))}
        </Stack>
      </Popover.Content>
    </Popover>
  );
}

export default AssignmentType;
